import {
  Box,
  lighten,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { ReactNode, useCallback, useEffect, useId, useMemo } from 'react';

import {
  ChangeType,
  GroupItemComponentProps,
  isPickListOption,
  ItemChangedFn,
} from '../../types';

import { yesCode } from '../../util/formUtil';
import { customVisuallyHidden } from '@/config/theme';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useDynamicFieldWatchValues } from '@/modules/form/hooks/rhf/useDynamicFieldWatchValues';
import { FormItem, ItemType } from '@/types/gqlTypes';

interface DisabilityGroupRow extends FormItem {
  type: ItemType.Group;
  item: [FormItem, FormItem];
}
interface DisabilityGroupItem extends FormItem {
  item: [DisabilityGroupRow];
}
function isValidDisabilityGroup(item: FormItem): item is DisabilityGroupItem {
  return (
    !!item.item &&
    item.item.every(
      (child) =>
        child.type === ItemType.Group && child.item && child.item.length === 2
    )
  );
}

const DisabilityTable = ({
  item,
  renderChildItem,
  itemChanged,
  severalItemsChanged,
  // values, - values are not used, this component manages it's own value subscription
}: GroupItemComponentProps) => {
  // Link ID for DisablingCondition, which is the last row in the table
  const disablingConditionLinkId = useMemo(() => {
    if (!isValidDisabilityGroup(item)) return;
    return item.item.slice(-1)[0].item.find((i) => i.type === ItemType.Choice)
      ?.linkId;
  }, [item]);

  // Link IDs for items that, if YES, constitute a Disabling Condition per HUD spec.
  // This is highly dependent on the structure of the item group.
  const dependentLinkIds = useMemo(() => {
    if (!isValidDisabilityGroup(item))
      throw new Error('incorrectly formatted disability table');

    // Get all rows except the last one (Disabling Condition)
    const disabilityRows = item.item.slice(0, -1);

    return disabilityRows.map((i) => {
      if (i.item[1].type === ItemType.Choice) {
        return i.item[1].linkId; // second col is "indefinite and impairs"
      } else {
        return i.item[0].linkId; // lack of second col indicates that disability is alway considered indefinite and impairing
      }
    });
  }, [item]);

  const values = useDynamicFieldWatchValues(dependentLinkIds);

  const dependentsThatAreYes = useMemo(() => {
    const result = Object.keys(values).filter(
      (k) => dependentLinkIds.indexOf(k) !== -1 && values[k]?.code === 'YES'
    );
    return result;
  }, [dependentLinkIds, values]);

  // Set DisablingCondition initially to YES if applicable.
  useEffect(() => {
    if (!itemChanged || !disablingConditionLinkId) return;
    if (dependentsThatAreYes.length > 0) {
      itemChanged({
        linkId: disablingConditionLinkId,
        value: yesCode,
        type: ChangeType.System,
      });
    }

    // DANGER: this doesn't need to run each time values change, just on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disablingConditionLinkId, itemChanged]);

  // Override itemChanged for dependents.
  const handleItemChanged: ItemChangedFn = useCallback(
    ({ linkId, value, type }) => {
      if (!itemChanged || !severalItemsChanged || !disablingConditionLinkId)
        return;

      const modifiesOverallDisablingCondition =
        dependentLinkIds.indexOf(linkId) !== -1;

      if (!modifiesOverallDisablingCondition) {
        return itemChanged({ linkId, value, type });
      }

      const wasOnlyRemainingYes =
        dependentsThatAreYes.length === 1 && dependentsThatAreYes[0] === linkId;

      // If the dependent is Yes, also update DisablingCondition to be Yes.

      const isChangedToYes =
        value && isPickListOption(value) && value.code === 'YES';
      if (isChangedToYes) {
        // Make "DisablingCondition" be yes, because this dependent is yes
        severalItemsChanged({
          values: { [linkId]: value, [disablingConditionLinkId]: yesCode },
          type,
        });
      } else if (wasOnlyRemainingYes) {
        // This was the last remaining "yes" that was forcing DisablingCondition to be yes.
        // So, we set DisablingCondition to NULL, so the user has to input a value.
        severalItemsChanged({
          values: { [linkId]: value, [disablingConditionLinkId]: null },
          type,
        });
      } else {
        itemChanged({ linkId, value, type });
      }
    },
    [
      itemChanged,
      severalItemsChanged,
      disablingConditionLinkId,
      dependentLinkIds,
      dependentsThatAreYes,
    ]
  );

  // IDs for accessibility labels
  const statusId = useId();
  const disablingConditionId = useId();
  const disabilityTypeId = useId();
  const isMobile = useIsMobile();

  function getDisabilityDropdowns(
    cellItem: FormItem,
    idx: number,
    disabilityTypeLabelId?: string,
    label?: ReactNode
  ) {
    return renderChildItem(cellItem, {
      noLabel: !label,
      inputProps: {
        label,
        placeholder: idx === 0 ? 'Select Status' : 'Select Disabling Condition',
        // Reads as "Physical Disability Status" or "Physical Disability Disabling Condition"
        ariaLabelledBy:
          disabilityTypeLabelId &&
          `${disabilityTypeLabelId} ${
            idx === 0 ? statusId : disablingConditionId
          }`,
      },
      disabled:
        (cellItem.linkId === disablingConditionLinkId &&
          dependentsThatAreYes.length > 0) ||
        false,
      itemChanged: handleItemChanged,
    });
  }

  return (
    <Table
      sx={{ border: (theme) => `1px solid ${theme.palette.grey[200]}`, mb: 2 }}
    >
      {isMobile ? (
        <TableHead>
          <TableRow>
            <TableCell>Disability Status</TableCell>
          </TableRow>
        </TableHead>
      ) : (
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={customVisuallyHidden}>Disability</Box>
            </TableCell>
            <TableCell id={statusId}>Status</TableCell>
            <TableCell id={disablingConditionId}>Disabling Condition</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody
        sx={{
          // Highlight last row
          '.MuiTableRow-root:last-child': {
            backgroundColor: (theme) => lighten(theme.palette.info.light, 0.9),
            td: {
              py: 2,
            },
          },
        }}
      >
        {isValidDisabilityGroup(item) &&
          item.item.map((rowItem, index) => {
            const disabilityTypeLabelId = `${disabilityTypeId}${index}`;
            return (
              <TableRow
                key={rowItem.linkId}
                sx={{
                  backgroundColor: (theme) =>
                    index & 1 ? undefined : theme.palette.grey[50],
                }}
              >
                <TableCell
                  sx={isMobile ? {} : { width: '25%', py: 3 }}
                  id={disabilityTypeLabelId}
                >
                  {!isMobile && (
                    <Typography variant='body2' fontWeight={600}>
                      {rowItem.text}
                    </Typography>
                  )}
                  {rowItem.helperText && (
                    <Typography variant='body2'>
                      {rowItem.helperText}
                    </Typography>
                  )}
                  {isMobile && (
                    <Stack direction='column' spacing={1}>
                      {rowItem.item.map((cellItem, idx) =>
                        getDisabilityDropdowns(
                          cellItem,
                          idx,
                          undefined,
                          cellItem.text
                        )
                      )}
                    </Stack>
                  )}
                </TableCell>
                {!isMobile &&
                  rowItem.item.map((cellItem, idx) => (
                    <TableCell
                      key={cellItem.linkId}
                      sx={{
                        pr: 2,
                        width: '33%',
                      }}
                    >
                      {getDisabilityDropdowns(
                        cellItem,
                        idx,
                        disabilityTypeLabelId
                        // don't pass a label because it is labelled-by the header cells
                      )}
                    </TableCell>
                  ))}
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};
export default DisabilityTable;
